/**
 * MeasurementService - Core measurement calculations using Turf.js
 *
 * Provides geodesic distance, area, and perimeter calculations,
 * coordinate formatting, and unit conversions.
 *
 * Validates: Requirements 19.1, 19.5, 19.6, 20.1, 20.5, 21.3
 */
import { point, lineString, polygon as turfPolygon } from '@turf/helpers';
import turfDistance from '@turf/distance';
import turfLength from '@turf/length';
import turfArea from '@turf/area';

// ─── Types ───────────────────────────────────────────────────────────────────

/** Turf distance unit strings (Units type removed from newer @turf/helpers) */
type TurfUnits = 'meters' | 'kilometres' | 'kilometers' | 'feet' | 'miles';

export type DistanceUnits = 'meters' | 'kilometers' | 'feet' | 'miles';
export type AreaUnits =
  | 'square-meters'
  | 'square-kilometers'
  | 'hectares'
  | 'square-feet'
  | 'square-miles'
  | 'acres';
export type CoordinateFormat = 'decimal-degrees' | 'degrees-minutes-seconds' | 'projected';

// ─── Unit Conversion Constants ───────────────────────────────────────────────

const AREA_CONVERSION_FACTORS: Record<AreaUnits, number> = {
  'square-meters': 1,
  'square-kilometers': 1e-6,
  hectares: 1e-4,
  'square-feet': 10.7639,
  'square-miles': 3.861e-7,
  acres: 0.000247105,
};

// ─── Distance Measurement ────────────────────────────────────────────────────

/**
 * Calculate the total geodesic distance along a series of points.
 * Points are in [longitude, latitude] format (WGS84).
 */
export function measureDistance(
  points: [number, number][],
  units: DistanceUnits = 'meters'
): number {
  if (points.length < 2) return 0;

  const turfUnits = mapDistanceUnits(units);
  let totalDistance = 0;

  for (let i = 0; i < points.length - 1; i++) {
    const from = point(points[i]);
    const to = point(points[i + 1]);
    totalDistance += turfDistance(from, to, { units: turfUnits });
  }

  return totalDistance;
}

/**
 * Calculate the geodesic distance between two points.
 */
export function measureSegmentDistance(
  from: [number, number],
  to: [number, number],
  units: DistanceUnits = 'meters'
): number {
  const turfUnits = mapDistanceUnits(units);
  return turfDistance(point(from), point(to), { units: turfUnits });
}

// ─── Area Measurement ────────────────────────────────────────────────────────

/**
 * Calculate the geodesic area of a polygon.
 * Coordinates should be in [longitude, latitude] format (WGS84).
 * The polygon ring must be closed (first point === last point).
 */
export function measureArea(
  polygonCoords: [number, number][],
  units: AreaUnits = 'square-meters'
): number {
  if (polygonCoords.length < 3) return 0;

  // Ensure the ring is closed
  const ring = ensureClosedRing(polygonCoords);
  const poly = turfPolygon([ring]);
  const areaInSqMeters = turfArea(poly);

  return areaInSqMeters * AREA_CONVERSION_FACTORS[units];
}

/**
 * Calculate the geodesic perimeter of a polygon.
 * Coordinates should be in [longitude, latitude] format (WGS84).
 */
export function measurePerimeter(
  polygonCoords: [number, number][],
  units: DistanceUnits = 'meters'
): number {
  if (polygonCoords.length < 3) return 0;

  const ring = ensureClosedRing(polygonCoords);
  const line = lineString(ring);
  const turfUnits = mapDistanceUnits(units);

  return turfLength(line, { units: turfUnits });
}

// ─── Coordinate Formatting ───────────────────────────────────────────────────

/**
 * Format a coordinate point into the specified format.
 * Input point is [longitude, latitude] in WGS84.
 */
export function formatCoordinates(point: [number, number], format: CoordinateFormat): string {
  const [lon, lat] = point;

  switch (format) {
    case 'decimal-degrees':
      return `${lat.toFixed(6)}°, ${lon.toFixed(6)}°`;

    case 'degrees-minutes-seconds':
      return `${decimalToDMS(lat, 'lat')}, ${decimalToDMS(lon, 'lon')}`;

    case 'projected':
      // Display as EPSG:3857 (Web Mercator) projected coordinates
      const [x, y] = lonLatToWebMercator(point);
      return `X: ${x.toFixed(2)}, Y: ${y.toFixed(2)}`;

    default:
      return `${lat.toFixed(6)}°, ${lon.toFixed(6)}°`;
  }
}

// ─── Unit Conversion ─────────────────────────────────────────────────────────

/**
 * Convert a distance value between units.
 */
export function convertDistanceUnits(
  value: number,
  fromUnits: DistanceUnits,
  toUnits: DistanceUnits
): number {
  if (fromUnits === toUnits) return value;

  // Convert to meters first, then to target units
  const metersValue = toMeters(value, fromUnits);
  return fromMeters(metersValue, toUnits);
}

/**
 * Convert an area value between units.
 */
export function convertAreaUnits(value: number, fromUnits: AreaUnits, toUnits: AreaUnits): number {
  if (fromUnits === toUnits) return value;

  // Convert to square meters first, then to target units
  const sqMetersValue = value / AREA_CONVERSION_FACTORS[fromUnits];
  return sqMetersValue * AREA_CONVERSION_FACTORS[toUnits];
}

// ─── Display Formatting ──────────────────────────────────────────────────────

/**
 * Format a distance value with appropriate precision and unit label.
 */
export function formatDistance(value: number, units: DistanceUnits): string {
  const unitLabels: Record<DistanceUnits, string> = {
    meters: 'm',
    kilometers: 'km',
    feet: 'ft',
    miles: 'mi',
  };

  if (value < 0.01) return `0 ${unitLabels[units]}`;

  const precision = value >= 100 ? 1 : value >= 1 ? 2 : 3;
  return `${value.toFixed(precision)} ${unitLabels[units]}`;
}

/**
 * Format an area value with appropriate precision and unit label.
 */
export function formatArea(value: number, units: AreaUnits): string {
  const unitLabels: Record<AreaUnits, string> = {
    'square-meters': 'm²',
    'square-kilometers': 'km²',
    hectares: 'ha',
    'square-feet': 'ft²',
    'square-miles': 'mi²',
    acres: 'ac',
  };

  if (value < 0.01) return `0 ${unitLabels[units]}`;

  const precision = value >= 100 ? 1 : value >= 1 ? 2 : 3;
  return `${value.toFixed(precision)} ${unitLabels[units]}`;
}

// ─── Helper Functions ────────────────────────────────────────────────────────

function mapDistanceUnits(units: DistanceUnits): TurfUnits {
  const mapping: Record<DistanceUnits, TurfUnits> = {
    meters: 'meters',
    kilometers: 'kilometers',
    feet: 'feet',
    miles: 'miles',
  };
  return mapping[units];
}

function toMeters(value: number, units: DistanceUnits): number {
  switch (units) {
    case 'meters':
      return value;
    case 'kilometers':
      return value * 1000;
    case 'feet':
      return value * 0.3048;
    case 'miles':
      return value * 1609.344;
  }
}

function fromMeters(meters: number, units: DistanceUnits): number {
  switch (units) {
    case 'meters':
      return meters;
    case 'kilometers':
      return meters / 1000;
    case 'feet':
      return meters / 0.3048;
    case 'miles':
      return meters / 1609.344;
  }
}

function ensureClosedRing(coords: [number, number][]): [number, number][] {
  if (coords.length === 0) return coords;
  const first = coords[0];
  const last = coords[coords.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) {
    return [...coords, first];
  }
  return coords;
}

/**
 * Convert decimal degrees to DMS (Degrees Minutes Seconds) string.
 */
function decimalToDMS(decimal: number, type: 'lat' | 'lon'): string {
  const absolute = Math.abs(decimal);
  const degrees = Math.floor(absolute);
  const minutesDecimal = (absolute - degrees) * 60;
  const minutes = Math.floor(minutesDecimal);
  const seconds = ((minutesDecimal - minutes) * 60).toFixed(2);

  let direction: string;
  if (type === 'lat') {
    direction = decimal >= 0 ? 'N' : 'S';
  } else {
    direction = decimal >= 0 ? 'E' : 'W';
  }

  return `${degrees}°${minutes}'${seconds}"${direction}`;
}

/**
 * Convert [longitude, latitude] to Web Mercator [x, y].
 */
function lonLatToWebMercator(lonLat: [number, number]): [number, number] {
  const [lon, lat] = lonLat;
  const x = (lon * 20037508.34) / 180;
  let y = Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) / (Math.PI / 180);
  y = (y * 20037508.34) / 180;
  return [x, y];
}
